import { Link } from "react-router-dom";
import { useApi } from "../lib/api";
import { useSession } from "../lib/session";
import { LIVENESS_COPY, rowState } from "../lib/liveness";

interface Alert { id: string; severity: string; summary: string }
interface HealthCheck { id: string; target: string; status: string }

/** Higher wins. Used to reduce a tenant's alerts / checks to a single worst state. */
const SEV_RANK: Record<string, number> = { critical: 4, high: 3, medium: 2, low: 1, warning: 1 };
const STATUS_RANK: Record<string, number> = { down: 3, degraded: 2, up: 1 };

function sevClass(sev: string): string {
  const s = sev.toLowerCase();
  if (s === "critical") return "s-crit";
  if (s === "high" || s === "medium") return "s-warn";
  return "s-muted";
}
function statusClass(status: string): string {
  const s = status.toLowerCase();
  if (s === "down") return "s-crit";
  if (s === "degraded") return "s-warn";
  if (s === "up") return "s-ok";
  return "s-muted";
}

/**
 * The tenant-aware header: what THIS tenant has right now.
 *
 * Unlike the two cards below (which report the hub's own health and the capability roster, and read
 * the same for everyone), this reflects the tenant the SESSION runs in — the one the API reports at
 * /v1/auth/session, which is the grant's `app_tenant`, never a value this page chose — so on the
 * finsetu tenant it shows FinSetu's targets, worst health, and open alerts, drawn live from the same
 * tenant-scoped, RLS-isolated endpoints the capability screens use.
 */
function TenantSummary() {
  const { session } = useSession();
  const tenant = session?.signedIn ? session.tenantId : "—";
  const role = session?.signedIn ? session.role : "—";
  const targets = useApi<{ serviceTargets: unknown[] }>("/v1/service-targets");
  const alerts = useApi<{ alerts: Alert[] }>("/v1/alerting/alerts");
  const checks = useApi<{ checks: HealthCheck[] }>("/v1/monitoring/checks");
  const audit = useApi<{ ok: boolean; window: { rows: number } }>("/v1/audit/verify");

  const loading = targets.loading || alerts.loading || checks.loading || audit.loading;
  const targetCount = targets.data?.serviceTargets.length ?? 0;

  const openAlerts = alerts.data?.alerts ?? [];
  const worstSev = openAlerts.reduce<string | null>(
    (m, a) => ((SEV_RANK[a.severity.toLowerCase()] ?? 0) > (m ? SEV_RANK[m] ?? 0 : 0) ? a.severity : m),
    null,
  );

  const checkList = checks.data?.checks ?? [];
  const worstStatus = checkList.reduce<string | null>(
    (m, c) => ((STATUS_RANK[c.status.toLowerCase()] ?? 0) > (m ? STATUS_RANK[m] ?? 0 : 0) ? c.status : m),
    null,
  );

  return (
    <div className="panel">
      <h2>Tenant · <span className="tenant-name">{tenant}</span> <span className="pill s-muted">{role}</span></h2>
      <p className="muted" style={{ marginTop: -4 }}>
        What this tenant has right now — drawn from its own RLS-scoped rows. The tenant and role are
        the session's, from your Rathvan ID grant.
        {import.meta.env.DEV && <> In development, <code>localStorage.plat_dev_tenant</code> / <code>plat_dev_role</code> pick them.</>}
      </p>
      {loading && <p className="muted">Loading…</p>}
      {!loading && (
        <div className="tiles" style={{ marginTop: 12 }}>
          <div className="tile">
            <div className="l">Service targets</div>
            <div className="n"><Link to="/targets">{targetCount}</Link></div>
          </div>
          <div className="tile">
            <div className="l">Health</div>
            <div className="n sm">
              {worstStatus
                ? <span className={`pill ${statusClass(worstStatus)}`}>{worstStatus}</span>
                : <span className="muted">no checks</span>}
              {checkList.length > 0 && <span className="muted" style={{ marginLeft: 8 }}>{checkList.length} checks</span>}
            </div>
          </div>
          <div className="tile">
            <div className="l">Open alerts</div>
            <div className="n">
              {openAlerts.length}
              {worstSev && <span className={`pill ${sevClass(worstSev)}`} style={{ marginLeft: 8, verticalAlign: "middle" }}>{worstSev}</span>}
            </div>
          </div>
          <div className="tile">
            <div className="l">Audit chain</div>
            <div className="n sm">
              {audit.data
                ? <><span className={`pill ${audit.data.ok ? "s-ok" : "s-crit"}`}>{audit.data.ok ? "verified" : "broken"}</span>
                    <span className="muted" style={{ marginLeft: 8 }}>{audit.data.window.rows} events</span></>
                : <span className="muted">—</span>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

interface Capability {
  id: string;
  title: string;
  summary: string;
  live: boolean;
}

interface CapabilitiesResponse {
  capabilities: Capability[];
  integrations: { port: string; adapter: string; live: boolean }[];
}

interface Health {
  ok: boolean;
  database: string;
  policyEngine: string;
  policyEngineLive: boolean;
  identityProvider: string;
}

export function Dashboard() {
  const health = useApi<Health>("/health");
  const caps = useApi<CapabilitiesResponse>("/v1/capabilities");

  const integrations = caps.data?.integrations ?? [];

  return (
    <>
      <TenantSummary />

      <div className="panel">
        <h2>Control-plane health</h2>
        <p className="muted" style={{ marginTop: -4 }}>
          The hub's own health and wiring — the same for every tenant, not tenant data.
        </p>
        {health.loading && <p className="muted">Checking…</p>}
        {health.error && <p className="err">{health.error.message}</p>}
        {health.data && (
          <div className="kv">
            <span className="k">Process</span><span>{health.data.ok ? "ok" : "degraded"}</span>
            <span className="k">Database</span><span>{health.data.database}</span>
            <span className="k">Policy engine</span>
            <span>
              {health.data.policyEngine}{" "}
              <span className={`pill ${health.data.policyEngineLive ? "live" : "planned"}`}>
                {health.data.policyEngineLive ? "live" : "fallback"}
              </span>
            </span>
            <span className="k">Identity provider</span><span>{health.data.identityProvider}</span>
          </div>
        )}
      </div>

      <div className="panel">
        <h2>Capabilities</h2>
        <p className="muted">
          The seven things platform-hub orchestrates. {LIVENESS_COPY}
        </p>
        {caps.loading && <p className="muted">Loading…</p>}
        {caps.error && <p className="err">{caps.error.message}</p>}
        {caps.data && (
          <table>
            <thead>
              <tr><th>Capability</th><th>What it does</th><th>State</th></tr>
            </thead>
            <tbody>
              {caps.data.capabilities.map((c) => (
                <tr key={c.id} data-testid="capability-row" data-capability={c.id}>
                  <td><Link to={`/c/${c.id}`}>{c.title}</Link></td>
                  <td className="muted">{c.summary}</td>
                  <td>
                    {(() => {
                      const st = rowState(c, integrations);
                      return (
                        <>
                          <span className={`pill ${st.label === "live" ? "live" : "planned"}`} data-testid="capability-state">{st.label}</span>
                          {st.reason && <div className="muted" data-testid="capability-reason">{st.reason}</div>}
                        </>
                      );
                    })()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}
